import { Crown, Zap, Star, Info, Building2, Check } from "lucide-react";
import { formatHoursMinutes } from "@/lib/video-metadata";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import Layout from "@/components/Layout";
import { useTranslation } from "react-i18next";
import { usePlansQuery, useMyDataQuery, useAddUserTagMutation } from "@/generated/graphql";
import { useMemo, useState, useEffect } from "react";
import { Switch } from "@/components/ui/switch";
import { createWhatsAppUrl } from "@/constants/contact";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCurrency, getPlanPrices } from "@/hooks/use-currency";
import { computeMaxYearlySavingsPct } from "@/lib/billing/computeMaxYearlySavingsPct";
import { WalletPlanDiscountNote } from "@/components/billing/WalletPlanDiscountNote";
import { PlanRenewalDate } from "@/components/billing/PlanRenewalDate";
import { useStoreCheckoutGuard } from "@/hooks/use-store-checkout-guard";
import PricingPlansClippers from "@/pages/PricingPlansClippers";

const PricingPlans = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const blockStoreCheckout = useStoreCheckoutGuard();
  const { formatCurrency, isBRL } = useCurrency();
  const { data, loading, error } = usePlansQuery();
  const apiPlans = (data?.plans ?? []).filter(Boolean).sort((a: any, b: any) => (a?.order ?? 0) - (b?.order ?? 0));
  const { data: myData } = useMyDataQuery();
  const [addUserTag] = useAddUserTagMutation();
  const subscriptionId = ((): string | undefined => {
    const sub: any = myData?.myData?.subscription;
    if (!sub) return undefined;
    if (typeof sub === 'object' && 'id' in sub) return String(sub.id);
    return typeof sub === 'string' || typeof sub === 'number' ? String(sub) : undefined;
  })();

  // Helper function to get subscription data
  const getSubscriptionData = () => {
    if (!myData?.myData?.subscription) return null;

    try {
      const subscription = typeof myData.myData.subscription === 'string'
        ? JSON.parse(myData.myData.subscription)
        : myData.myData.subscription;

      return subscription;
    } catch (error) {
      console.error('Error parsing subscription data:', error);
      return null;
    }
  };

  const subscriptionData = getSubscriptionData();
  const currentPlan = subscriptionData?.plan;

  // Function to check if a plan is the user's current plan
  const isCurrentPlan = (plan: any) => {
    if (!currentPlan) return false;

    // For basic plan (free), check if user has no paid subscription
    if (plan.slug === 'basico') {
      return !currentPlan || currentPlan.value === 0 || currentPlan.value === '0';
    }

    // For paid plans, match by slug or documentId
    return plan.slug === currentPlan.slug ||
           plan.documentId === currentPlan.documentId ||
           plan.name === currentPlan.name;
  };
  const basicPlan = {
    name: t('common.free'),
    slug: 'basico',
    value: 0,
    processing_seconds: 60 * 60,
    max_clips: 5, // backend FREE plan (autoCreatePlans.js)
    description_items: [
      { label: t('pricing.withWatermark') },
      { label: t('pricing.lowerPriorityRendering'), tooltip: t('pricing.lowerPriorityRenderingTooltip') },
    ],
    payment_link: '/register',
    discount_label: undefined,
    color: '#9ca3af', // gray-400
    order: 0,
    max_expiry_days: 7,
  } as any;
  const plans = [basicPlan, ...apiPlans];
  const [isYearly, setIsYearly] = useState(() => searchParams.get('yearly') === 'true');

  // Track user accessing the plans page
  useEffect(() => {
    const trackPlanPageVisit = async () => {
      try {
        await addUserTag({ variables: { name: 'checked-plans-page' } });
      } catch (error) {
        console.error('Error adding user tag:', error);
      }
    };

    trackPlanPageVisit();
  }, [addUserTag]);
  const maxYearlyDiscountPct = useMemo(
    () => computeMaxYearlySavingsPct(apiPlans as any[], isBRL),
    [apiPlans, isBRL],
  );

  const getIconForSlug = (slug?: string | null) => {
    switch (slug) {
      case "ouro":
        return Crown;
      case "prata":
        return Star;
      case "bronze":
      default:
        return Zap;
    }
  };

  const buildFeatures = (plan: any): Array<{ label: string; comingSoon?: boolean; tooltip?: string }> => {
    const items: Array<{ label: string; comingSoon?: boolean; tooltip?: string }> = [];
    const totalSecs = Number(plan?.processing_seconds ?? 0);
    if (totalSecs > 0) items.push({ label: t('pricing.processingTime', { time: formatHoursMinutes(totalSecs) }) });
    const maxClips = Number(plan?.max_clips ?? 0);
    if (maxClips > 0) items.push({ label: maxClips >= 1000000 ? t('pricing.unlimitedClips') : t('pricing.clipsPerMonth', { clips: maxClips }) });
    const maxExpiryDays = Number(plan?.max_expiry_days ?? 0);
    if (maxExpiryDays > 0) items.push({ label: t('pricing.projectsKeptDays', { days: maxExpiryDays }), tooltip: t('pricing.projectsKeptDaysTooltip') });
    const planValue = Number(plan?.value ?? 0);
    if (planValue > 0) items.push({ label: t('pricing.priorityRendering'), tooltip: t('pricing.priorityRenderingTooltip') });
    const desc = Array.isArray(plan?.description_items) ? plan.description_items : [];
    for (const d of desc) {
      if (d && typeof d === "object" && "label" in d && (!d.for || d.for === 'own_brand')) {
        const label = isBRL ? String((d as any).label) : String((d as any).label_en || (d as any).label);
        items.push({ label, comingSoon: Boolean((d as any).coming_soon), tooltip: (d as any).tooltip || undefined });
      } else if (typeof d === "string") {
        items.push({ label: d });
      }
    }
    return items;
  };


  const handleCheckout = (plan: any) => {
    if (!plan.documentId) {
      return;
    }
    if (blockStoreCheckout(subscriptionData)) return;

    // Pegar o parâmetro coupon da URL atual se existir
    const couponParam = searchParams.get('coupon');

    // Construir a URL com os parâmetros
    const params = new URLSearchParams();
    params.set('yearly', String(isYearly));
    if (couponParam) {
      params.set('coupon', couponParam);
    }

    // Navegar para a página de checkout com o plano selecionado
    navigate(`/plans/${plan.documentId}?${params.toString()}`);
  };

  const isOtherChannels = myData?.myData?.onboarding_objective === 'other_channels';

  if (isOtherChannels) {
    return (
      <Layout onStartTour={() => {}}>
        <PricingPlansClippers
          plans={apiPlans}
          loading={loading}
          error={error}
          currentPlan={currentPlan}
          subscription={subscriptionData}
          subscriptionId={subscriptionId}
        />
      </Layout>
    );
  }

  return (
    <Layout
      onStartTour={() => {}}
    >
      <div className="flex-1 p-4 lg:p-6">
        <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-primary bg-clip-text text-transparent mb-4">
            {t('pricing.title')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('pricing.subtitle')}
          </p>
          {error && (
            <p className="text-sm text-destructive mt-2">{t('pricing.failedToLoadPlans')}</p>
          )}
        </div>

        <div className="flex items-center justify-center gap-3 mb-10">
          <span className={`text-sm ${!isYearly ? 'font-semibold' : 'text-muted-foreground'}`}>{t('pricing.monthly')}</span>
          <Switch checked={isYearly} onCheckedChange={setIsYearly} />
          <span className={`text-sm ${isYearly ? 'font-semibold' : 'text-muted-foreground'}`}>{t('pricing.yearly')}</span>
          {maxYearlyDiscountPct > 0 && (
            <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded border border-primary/20">
              {t('pricing.saveUpTo', { percent: maxYearlyDiscountPct })}
            </span>
          )}
        </div>
        {loading ? (
          <div className="text-center text-sm text-muted-foreground">{t('pricing.loadingPlans')}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {plans.map((plan: any, index: number) => {
              const IconComponent = getIconForSlug(plan?.slug ?? undefined);
              const isPopular = (plan?.order ?? 0) === 2 || (plan?.slug ?? '') === 'prata';
              const isCurrent = isCurrentPlan(plan);
              const { value: priceValue, valueYearly: yearlyTotal } = getPlanPrices(plan, isBRL);
              const yearlyMonthlyEquivalent = yearlyTotal > 0 ? yearlyTotal / 12 : 0;

              const features = buildFeatures(plan);
              return (
                <Card
                  key={plan?.slug ?? index}
                  className={`relative overflow-hidden transition-all duration-300 ${
                    isCurrent
                      ? 'border-primary bg-gradient-to-br from-primary/10 to-primary/20 opacity-80'
                      : isPopular
                      ? 'border-primary/40 shadow-glow bg-gradient-to-br from-primary/5 to-primary/10 hover:scale-105'
                      : 'border-border hover:border-primary/20 hover:scale-105'
                  }`}
                >
                  {isCurrent && (
                    <div className="absolute top-0 left-0 right-0">
                      <div className="bg-primary/10 text-primary font-bold text-center py-2 text-sm font-medium">
                        {t('pricing.currentPlan')}
                      </div>
                    </div>
                  )}
                  {!isCurrent && isPopular && (
                    <div className="absolute top-0 left-0 right-0">
                      <div className="bg-gradient-primary text-black font-bold text-center py-2 text-sm font-medium">
                        {t('pricing.mostPopular')}
                      </div>
                    </div>
                  )}

                  <CardHeader className={`text-center ${isCurrent || isPopular ? 'pt-12' : 'pt-6'}`}>
                    <div className={`w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4`} style={{ border: `2px solid ${plan?.color ?? 'transparent'}` }}>
                      <IconComponent className={`w-8 h-8`} style={{ color: plan?.color ?? undefined }} />
                    </div>

                    <CardTitle className="text-2xl font-bold">{plan?.name}</CardTitle>
                    {isYearly ? (
                      <>
                        {yearlyTotal > 0 ? (
                          <>
                            <p className="text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                              {formatCurrency(yearlyMonthlyEquivalent)}
                              <span className="text-base text-muted-foreground font-normal">/{t('common.perMonth').split(' ')[1]}</span>
                            </p>
                            <div className="text-center">
                              <span className="text-sm text-muted-foreground">{t('common.billedAnnually')}</span>
                              <p className="text-sm text-muted-foreground mt-1">
                                {t('checkout.annualTotal')} {formatCurrency(yearlyTotal)}
                              </p>
                              <WalletPlanDiscountNote
                                plan={plan}
                                priceReais={yearlyMonthlyEquivalent}
                                formatCurrency={formatCurrency}
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                              {t('common.free')}
                            </p>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {priceValue > 0 ? (
                          <>
                            <p className="text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                              {formatCurrency(priceValue)}
                            </p>
                            <div className="text-center">
                              <span className="text-base">{t('common.perMonth')}</span>
                              <div>
                                <WalletPlanDiscountNote
                                  plan={plan}
                                  priceReais={priceValue}
                                  formatCurrency={formatCurrency}
                                />
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                              {t('common.free')}
                            </p>
                          </>
                        )}
                      </>
                    )}
                  </CardHeader>

                  <CardContent className="space-y-6">
                    {priceValue > 0 && (
                      <Button
                        variant={isCurrent ? 'secondary' : isPopular ? 'default' : 'outline'}
                        className="w-full"
                        size="lg"
                        disabled={isCurrent}
                        onClick={() => !isCurrent && handleCheckout(plan)}
                      >
                        {isCurrent
                          ? t('pricing.currentPlan')
                          : isYearly
                          ? t('pricing.selectYearlyPlan')
                          : t('pricing.selectPlan')
                        }
                      </Button>
                    )}
                    {priceValue === 0 && isCurrent && (
                      <Button
                        variant="secondary"
                        className="w-full"
                        size="lg"
                        disabled={true}
                      >
                        {t('pricing.currentPlan')}
                      </Button>
                    )}
                    {isCurrent && (
                      <PlanRenewalDate subscription={subscriptionData} className="items-center" />
                    )}

                    <div className="space-y-3">
                      {features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                          <span className={`text-sm ${feature.comingSoon ? 'text-muted-foreground' : ''}`}>
                            {feature.label}{feature.comingSoon ? ` (${t('common.comingSoon')})` : ''}
                            {feature.tooltip && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="w-3.5 h-3.5 text-muted-foreground inline-block ml-1 cursor-help align-text-bottom" />
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs">
                                  {feature.tooltip}
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Enterprise Section */}
        <Card className="max-w-4xl mx-auto mt-16 bg-gradient-to-br from-secondary/50 to-card border-primary/20">
          <CardContent className="p-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-primary flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-black" />
                  </div>
                  <h2 className="text-3xl font-bold">{t('common.enterprise')}</h2>
                </div>
                <p className="text-lg text-muted-foreground mb-6">
                  {t('pricing.plans.enterprise.description')}
                </p>
                <ul className="space-y-3 mb-6">
                  {(t('pricing.plans.enterprise.features', { returnObjects: true }) as string[]).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-center md:text-right">
                <div className="mb-6">
                  <div className="text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent mb-2">
                    {t('pricing.plans.enterprise.price')}
                  </div>
                  <p className="text-muted-foreground">{t('pricing.plans.enterprise.priceSubtitle')}</p>
                </div>
                <Button size="lg" className="bg-gradient-primary hover:opacity-90" asChild>
                  <a
                    href={createWhatsAppUrl("Hello! I'd like to know more about the AutoClipper Enterprise plan.")}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('common.talkToSpecialist')}
                  </a>
                </Button>
                <p className="text-xs text-muted-foreground mt-4">
                  {t('common.responseIn24h')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-12">
          <p className="text-muted-foreground">
            {t('pricing.haveQuestions')}{" "}
            <a
              href={createWhatsAppUrl("Hello! I'd like to know more about AutoClipper plans and get a custom plan.")}
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline font-medium"
            >
              {t('pricing.contactSupport')}
            </a>{" "}
            {t('pricing.customPlanMessage')}
          </p>
        </div>
        </div>
      </div>
    </Layout>
  );
};

export default PricingPlans;
